import { useState, useRef, useEffect } from "react";
import { Volume2, Pause, Play, Loader2, User, UserCircle, Square } from "lucide-react";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Slider } from "./ui/slider";
import { toast } from "sonner";

const AudioPlayer = ({ text, isPremium, onPremiumRequired }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [voiceType, setVoiceType] = useState("female");
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(null);
  const [rate, setRate] = useState(0.9);
  const [availableVoices, setAvailableVoices] = useState([]);
  const utteranceRef = useRef(null);

  // Load available voices (retry — Android needs time to populate voices)
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;

    let cancelled = false;
    let attempts = 0;

    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      if (voices.length === 0 && attempts < 10 && !cancelled) {
        attempts += 1;
        setTimeout(loadVoices, 250);
        return;
      }
      const frenchVoices = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith('fr'));
      setAvailableVoices(frenchVoices);
    };

    loadVoices();
    speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      cancelled = true;
      speechSynthesis.onvoiceschanged = null;
      speechSynthesis.cancel();
    };
  }, []);

  const getVoice = () => {
    const frenchVoices = availableVoices;
    
    if (frenchVoices.length === 0) return null;

    // If user manually selected a voice, use it
    if (selectedVoiceIndex !== null && frenchVoices[selectedVoiceIndex]) {
      return frenchVoices[selectedVoiceIndex];
    }

    // Try to find a voice matching the preference
    if (voiceType === "female") {
      const femaleVoice = frenchVoices.find(v => {
        const name = v.name.toLowerCase();
        return name.includes('female') || 
               name.includes('femme') ||
               name.includes('amélie') ||
               name.includes('aurelie') ||
               name.includes('marie') ||
               name.includes('celine') ||
               name.includes('pauline') ||
               name.includes('hortense') ||
               // Google voices
               name.includes('fr-fr-standard-a') ||
               name.includes('fr-fr-standard-c') ||
               name.includes('fr-fr-wavenet-a') ||
               name.includes('fr-fr-wavenet-c');
      });
      if (femaleVoice) return femaleVoice;
    } else {
      const maleVoice = frenchVoices.find(v => {
        const name = v.name.toLowerCase();
        return name.includes('male') || 
               name.includes('homme') ||
               name.includes('thomas') ||
               name.includes('pierre') ||
               name.includes('henri') ||
               name.includes('daniel') ||
               name.includes('claude') ||
               // Google voices
               name.includes('fr-fr-standard-b') ||
               name.includes('fr-fr-standard-d') ||
               name.includes('fr-fr-wavenet-b') ||
               name.includes('fr-fr-wavenet-d');
      });
      if (maleVoice) return maleVoice;
    }

    // Fallback: return first voice, but prefer one matching gender if possible
    if (voiceType === "male" && frenchVoices.length > 1) {
      return frenchVoices[1]; // Often the second voice is male
    }
    
    return frenchVoices[0];
  };

  const speak = () => {
    if (!isPremium) {
      onPremiumRequired?.();
      return;
    }

    if (!text || text.trim().length === 0) {
      toast.error("Aucun texte à lire");
      return;
    }

    if (!('speechSynthesis' in window)) {
      toast.error("Votre navigateur ne supporte pas la synthèse vocale");
      return;
    }

    // Try to find a French voice, but don't block if none found.
    // The OS TTS engine can still read French text with lang='fr-FR' even
    // without an explicit French voice (Android falls back to the default voice).
    const voice = getVoice();
    if (!voice && availableVoices.length === 0) {
      toast.warning("Aucune voix française installée", {
        description: "Lecture avec la voix par défaut. Pour une meilleure qualité : Paramètres Android → Général → Synthèse vocale → Installer voix française.",
        duration: 6000,
      });
    }

    // Cancel any ongoing speech
    speechSynthesis.cancel();

    setIsLoading(true);

    // Split text into chunks if too long (max 4000 chars for mobile)
    const MAX_CHUNK_SIZE = 4000;
    const textToSpeak = text.substring(0, MAX_CHUNK_SIZE);
    
    if (text.length > MAX_CHUNK_SIZE) {
      toast.info(`Lecture des ${MAX_CHUNK_SIZE} premiers caractères`, {
        description: "Les textes trop longs sont limités sur mobile"
      });
    }

    // Create utterance
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = 'fr-FR';
    utterance.rate = rate;
    utterance.pitch = 1;
    utterance.volume = 1;
    // Only set voice if we actually found one — otherwise let the OS pick
    // the default voice for fr-FR (works better on Android without French packs)
    if (voice) {
      utterance.voice = voice;
    }

    utterance.onstart = () => {
      setIsLoading(false);
      setIsPlaying(true);
    };

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utterance.onerror = (e) => {
      console.error('Speech error:', e);
      setIsLoading(false);
      setIsPlaying(false);
      
      if (e.error !== 'canceled') {
        let errorMsg = "Erreur de lecture audio";
        let errorDesc = "";
        
        switch(e.error) {
          case 'network':
            errorMsg = "Erreur réseau";
            errorDesc = "Vérifiez votre connexion Internet";
            break;
          case 'synthesis-failed':
            errorMsg = "Échec de la synthèse vocale";
            errorDesc = "Essayez de redémarrer l'app ou changez de voix";
            break;
          case 'synthesis-unavailable':
            errorMsg = "Synthèse vocale indisponible";
            errorDesc = "Activez la synthèse vocale dans les paramètres Android";
            break;
          case 'audio-busy':
            errorMsg = "Audio occupé";
            errorDesc = "Une autre app utilise l'audio";
            break;
          case 'not-allowed':
            errorMsg = "Permission refusée";
            errorDesc = "Autorisez l'audio dans les paramètres";
            break;
          default:
            errorDesc = `Code: ${e.error}`;
        }
        
        toast.error(errorMsg, errorDesc ? { description: errorDesc } : undefined);
      }
    };

    utteranceRef.current = utterance;
    
    // Small delay helps on some Android devices
    setTimeout(() => {
      speechSynthesis.speak(utterance);
    }, 100);
  };

  const stop = () => {
    speechSynthesis.cancel();
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      if (speechSynthesis.paused) {
        speechSynthesis.resume();
      } else {
        speechSynthesis.pause();
        setIsPlaying(false);
      }
    } else {
      if (speechSynthesis.paused) {
        speechSynthesis.resume();
        setIsPlaying(true);
      } else {
        speak();
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="border-gold-leaf text-gold-leaf hover:bg-gold-leaf/10"
            disabled={isLoading}
            data-testid="audio-settings"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
            <span className="ml-2 hidden sm:inline">Écouter</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 bg-paper border-border" align="end">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-body text-ink-primary mb-3 block">
                Choisir une voix
              </Label>
              <RadioGroup value={voiceType} onValueChange={setVoiceType} className="space-y-2">
                <div className="flex items-center space-x-3 p-2 rounded-sm hover:bg-paper-dark cursor-pointer">
                  <RadioGroupItem value="female" id="voice-female" />
                  <Label htmlFor="voice-female" className="flex items-center gap-2 cursor-pointer">
                    <UserCircle className="h-4 w-4 text-pink-500" />
                    <span className="font-body">Voix féminine</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-2 rounded-sm hover:bg-paper-dark cursor-pointer">
                  <RadioGroupItem value="male" id="voice-male" />
                  <Label htmlFor="voice-male" className="flex items-center gap-2 cursor-pointer">
                    <User className="h-4 w-4 text-blue-500" />
                    <span className="font-body">Voix masculine</span>
                  </Label>
                </div>
              </RadioGroup>
              
              {availableVoices.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border">
                  <Label className="text-xs font-body text-ink-secondary mb-2 block">
                    Ou choisir manuellement :
                  </Label>
                  <select 
                    className="w-full text-xs p-2 rounded border border-border bg-paper text-ink-primary font-body"
                    value={selectedVoiceIndex ?? ''}
                    onChange={(e) => setSelectedVoiceIndex(e.target.value === '' ? null : parseInt(e.target.value))}
                  >
                    <option value="">Automatique</option>
                    {availableVoices.map((voice, idx) => (
                      <option key={idx} value={idx}>
                        {voice.name} {voice.localService ? '(Local)' : '(En ligne)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-body text-ink-primary mb-2 block">
                Vitesse: {rate.toFixed(1)}x
              </Label>
              <Slider
                value={[rate]}
                onValueChange={([v]) => setRate(v)}
                min={0.5}
                max={1.5}
                step={0.1}
                className="w-full"
              />
            </div>
            
            <Button
              onClick={speak}
              disabled={isLoading || isPlaying}
              className="w-full bg-gold-leaf hover:bg-gold-leaf/90 text-ink-primary"
              data-testid="generate-audio"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Lire le chapitre
                </>
              )}
            </Button>
            
            {!isPremium && (
              <p className="text-xs text-ink-secondary text-center">
                Fonctionnalité Premium
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
      
      {isPlaying && (
        <Button
          variant="ghost"
          size="icon"
          onClick={stop}
          className="text-rubric-red hover:text-rubric-red/80"
          data-testid="stop-audio"
        >
          <Square className="h-4 w-4 fill-current" />
        </Button>
      )}
    </div>
  );
};

export default AudioPlayer;
